import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import type { ObservableQuery } from '@apollo/client'
import type { QueryRef } from 'apollo-angular'
import { Apollo } from 'apollo-angular'
import { Observable, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { environment } from '../../environments/environment'
import type { DateRange } from '../components/filter-group/components/filters/components/components/filter-date-range'
import type {
  ArticleOptions,
  ArticleType,
  EditionFilter,
  FetchPolicy,
} from '../types/types'
import { ARTICLE_TYPE_BACKEND_TITLES } from '../types/types'
import { completeDataOrThrow } from './complete-data.operator'
import {
  ACCEPT_ORGANIZATION_RESELLER_REQUEST,
  ADD_DOMAINNAME,
  CANCEL_SCHEDULED_CHANGE,
  CONFIGURE_SUBSCRIPTIONS,
  CREATE_NOTIFICATION_OPT_OUT,
  CREATE_ORGANIZATION,
  CREATE_ORGANIZATION_RESELLER_REQUEST,
  CREATE_SEAT_ALLOCATION,
  CREATE_USER,
  DELETE_NOTIFICATION_OPT_OUT,
  DELETE_ORGANIZATION_RESELLER_REQUEST,
  DELETE_SEAT_ALLOCATION,
  FORGOT_PASSWORD,
  PURCHASE_ARTICLE,
  REJECT_ORGANIZATION_RESELLER_REQUEST,
  REMOVE_DOMAINNAME,
  REMOVE_RESELLER,
  REQUEST_SEAT,
  RESET_PASSWORD,
  UPDATE_BILLING_CYCLE,
  UPDATE_ORGANIZATION,
  UPDATE_USER,
  VERIFY_USER,
} from './mutation'
import {
  ARTICLE_INDEX,
  GET_ALL_FAQS_QUERY,
  GET_ARTICLES_BY_AUTHOR_ID,
  GET_ARTICLES_BY_EDITION_ID,
  GET_ARTICLES_BY_MAGAZINE_ID,
  GET_ARTICLE_BY_ID,
  GET_ARTICLE_DETAILS,
  GET_ARTICLE_ID_BY_OLDID,
  GET_ARTICLE_ID_BY_SENEID,
  GET_AUTHORS,
  GET_AUTHOR_BY_ID,
  GET_BILLING_CYCLES,
  GET_DETAILED_MAGAZINES,
  GET_DOMAINS_BY_ORG,
  GET_EDITIONS,
  GET_EDITIONS_BY_MAGAZINE_ID,
  GET_EDITION_BY_ID,
  GET_EDITORIAL_BOARD,
  GET_EDITORS,
  GET_INVOICES,
  GET_MAGAZINES,
  GET_MAGAZINE_BY_ARTICLE_ID,
  GET_MAGAZINE_BY_ID,
  GET_NOTIFICATION_OPT_OUTS,
  GET_ORGANIZATION_BY_ID,
  GET_ORGANIZATION_FOR_RESELLER,
  GET_ORGANIZATION_RESELLER_REQUESTS,
  GET_ORGANIZATION_RESELLER_REQUEST_BY_ID,
  GET_PAGE_BY_SLUG_QUERY,
  GET_PRODUCTS,
  GET_PRORATION_FACTOR,
  GET_RANDOM_ADVERTISEMENT,
  GET_RESELLERS,
  GET_SUBSCRIPTIONS,
  GET_SUBSCRIPTIONS_AND_PRICING,
  GET_TOTAL_ARTICLES_COUNT,
  GET_USERS_BY_ORGANIZATION,
  GET_USER_ARTICLE_PURCHASE_BY_ID,
  GET_USER_COUNT_BY_ORGANIZATION,
  GET_USER_MAGAZINES,
  GET_USER_ME,
  LOGIN_USER,
  SEARCH_AUTHORS,
  SEAT_ALLOCATION_BY_SUBSCRIPTION,
  SEAT_ALLOCATION_BY_USER,
} from './queries'
import type { MagazineOptions } from './queries'
import { GET_SELECTION_AND_ESSENCE_SOURCES } from './queries/articles/get-selection-and-essence-sources.query'
import { GET_AUTHOR_FILTER_ITEMS } from './queries/authors/get-author-filter-items.query'
import { VALIDATE_ORGANIZATION_DOMAINS } from './queries/domainnames/validate-organization-domains.query'
import { GET_MAGAZINE_FILTER_ITEMS } from './queries/magazines/get-magazine-filter-items.query'
import { GET_ORGANIZATION_FILTER_ITEMS } from './queries/organization/get-organization-filter-items.query'
import { GET_ORGANIZATION_INVOICE_PRICES } from './queries/organization/get-organization-invoice-prices.query'
import { GET_SUBSCRIPTION_AND_PRICING } from './queries/subscriptions/get-subscription-and-price.query'
import {
  AdvertisementPlacement,
  ArticleDetailsDto,
  ArticlePurchaseOptions,
  ArticlesDto,
  ArticlesResponseDto,
  AuthorDto,
  AuthorFilterItemsResponseDto,
  AuthorsResponseDto,
  CancelScheduledChangeResponseDto,
  ConfigureSubscriptionsResponseDto,
  CreateAccountResponseDto,
  CreateDomainNameResponseDto,
  CreateNotificationOptOutResponseDto,
  CreateUserResponseDto,
  DeleteDomainNameResponseDto,
  DeleteNotificationOptOutResponseDto,
  DomainNamesResponseDto,
  EditionDto,
  EditionsResponseDto,
  EditorialBoardResponseDto,
  EditorsResponseDto,
  FAQResponseDto,
  GetProrationFactorDto,
  ListInvoicesResponseDto,
  LoginUserResponseDto,
  MagazineDto,
  MagazineFilterItemsResponseDto,
  MagazinesResponseDto,
  NotificationOptOutsResponseDto,
  OrganizationDto,
  PageResponseDto,
  Pagination,
  ProductsResponseDto,
  PurchaseArticleCustomerInput,
  PurchaseArticleResponseDto,
  RandomAdvertisementResponseDto,
  ResetPasswordResponseDto,
  SelectionAndEssenceSourcesResponseDto,
  SubscriptionResponseDto,
  SubscriptionsResponseDto,
  TotalArticlesCountResponseDto,
  UpdateUserResponseDto,
  UserArticlePurchaseResponseDto,
  UserCountResponseDto,
  UserDto,
  UserMeResponseDto,
  UsersResponseDto,
  ValidateOrganizationDomainsResponse,
  VerifyUserMutationResponseDto,
  VerifyUserResponseDto,
} from './responses'
import type { MagazineSubscriptionResponseDto } from './responses/magazines/magazine.dto'
import {
  AcceptOrganizationResellerRequestResponseDto,
  AvailableResellersResponseDto,
  BillingCyclesResponseDto,
  CreateOrganizationResellerRequestResponseDto,
  CreateOrganizationResponseDto,
  DeleteOrganizationResellerRequestResponseDto,
  OrganizationFilterItemsResponseDto,
  OrganizationInvoicePricesResponseDto,
  OrganizationResellerRequestResponseDto,
  OrganizationResellerRequestsResponseDto,
  OrganizationResponseDto,
  OrganizationsResponseDto,
  RejectOrganizationResellerRequestResponseDto,
  RemoveResellerResponseDto,
  UpdateBillingCycleResponseDto,
  UpdateOrganizationResponseDto,
} from './responses/organization'
import type {
  CreateSeatAllocationResponseDto,
  DeleteSeatAllocationResponseDto,
  SeatAllocationsResponseDto,
} from './responses/seats'

function startOfToday(): string {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date.toISOString()
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly apollo = inject(Apollo)
  private readonly http = inject(HttpClient)

  requestSeat(
    magazineId: number
  ): Observable<Apollo.MutateResult<{ requestSeat: { success: boolean } }>> {
    return this.apollo.mutate<{ requestSeat: { success: boolean } }>({
      mutation: REQUEST_SEAT,
      variables: { magazineId },
    })
  }

  getMagazines(
    page: number,
    itemsPerPage: number
  ): Observable<ObservableQuery.Result<MagazinesResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<MagazinesResponseDto>({
        query: GET_MAGAZINES,
        variables: { page, itemsPerPage },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getDetailedMagazines(
    page: number,
    itemsPerPage: number
  ): Observable<ObservableQuery.Result<MagazinesResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<MagazinesResponseDto>({
        query: GET_DETAILED_MAGAZINES,
        variables: { page, itemsPerPage },
        // The magazine catalog rarely changes within a session: serve from
        // the Apollo cache once populated instead of refetching on every call.
        fetchPolicy: 'cache-first',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getSelectionAndEssenceSources(
    selectionAndEssenceId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<
    ObservableQuery.Result<SelectionAndEssenceSourcesResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<SelectionAndEssenceSourcesResponseDto>({
        query: GET_SELECTION_AND_ESSENCE_SOURCES,
        variables: { selectionAndEssenceId },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getMagazineById(
    id: number,
    options: MagazineOptions
  ): Observable<ObservableQuery.Result<{ Magazine: MagazineDto }, 'complete'>> {
    return this.apollo
      .watchQuery<{ Magazine: MagazineDto }>({
        query: GET_MAGAZINE_BY_ID,
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        variables: { id, ...options },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getMagazineFilterItems(): Observable<
    ObservableQuery.Result<MagazineFilterItemsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<MagazineFilterItemsResponseDto>({
        query: GET_MAGAZINE_FILTER_ITEMS,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getAuthorFilterItems(): Observable<
    ObservableQuery.Result<AuthorFilterItemsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<AuthorFilterItemsResponseDto>({
        query: GET_AUTHOR_FILTER_ITEMS,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getOrganizationFilterItems(
    resellerId: number
  ): Observable<
    ObservableQuery.Result<OrganizationFilterItemsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<OrganizationFilterItemsResponseDto>({
        query: GET_ORGANIZATION_FILTER_ITEMS,
        variables: { resellerId },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getOrganizationInvoicePrices(): Observable<
    ObservableQuery.Result<OrganizationInvoicePricesResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<OrganizationInvoicePricesResponseDto>({
        query: GET_ORGANIZATION_INVOICE_PRICES,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getEditions(): Observable<
    ObservableQuery.Result<EditionsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<EditionsResponseDto>({
        query: GET_EDITIONS,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getAuthors(
    page: number,
    limit: number,
    filter?: Record<string, unknown>,
    sort?: string,
    where?: Record<string, unknown>
  ): Observable<ObservableQuery.Result<AuthorsResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<AuthorsResponseDto>({
        query: GET_AUTHORS,
        variables: { page, limit, filter, sort, where },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getAuthorById(
    id: number
  ): Observable<ObservableQuery.Result<{ Author: AuthorDto }, 'complete'>> {
    return this.apollo
      .watchQuery<{ Author: AuthorDto }>({
        query: GET_AUTHOR_BY_ID,
        variables: { id },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getEditionsByMagazineId(
    magazineId: number,
    pagination: Partial<Pagination> = {},
    filter: EditionFilter = {}
  ): QueryRef<EditionsResponseDto> {
    return this.apollo.watchQuery<EditionsResponseDto>({
      query: GET_EDITIONS_BY_MAGAZINE_ID,
      variables: {
        filter: {
          ...filter,
          magazine: { equals: magazineId },
        },
        ...pagination,
      },
    })
  }

  getEditionById(
    id: number
  ): Observable<ObservableQuery.Result<{ Edition: EditionDto }, 'complete'>> {
    return this.apollo
      .watchQuery<{ Edition: EditionDto }>({
        query: GET_EDITION_BY_ID,
        variables: { id },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getMagazineByArticleId(
    articleId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<
    ObservableQuery.Result<{ Article: { magazine: MagazineDto } }, 'complete'>
  > {
    return this.apollo
      .watchQuery<{ Article: { magazine: MagazineDto } }>({
        query: GET_MAGAZINE_BY_ARTICLE_ID,
        variables: { articleId },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getEditionByArticleId(
    articleId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<
    ObservableQuery.Result<{ Article: { edition: EditionDto } }, 'complete'>
  > {
    return this.apollo
      .watchQuery<{ Article: { edition: EditionDto } }>({
        query: GET_ARTICLE_BY_ID,
        variables: { articleId },
        fetchPolicy,
      })
      .valueChanges.pipe(
        completeDataOrThrow(),
        map(result => ({
          ...result,
          data: {
            Article: {
              edition: result.data.Article.edition,
            },
          },
        }))
      )
  }

  getArticlesByEditionId(
    editionId: number,
    options: ArticleOptions,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<Apollo.QueryResult<ArticlesResponseDto>> {
    // Format editionId as an operator object and ensure fetchDetails is true
    const queryOptions = {
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...options,
      fetchDetails: true,
      editionId: { equals: editionId },
    }

    return this.apollo.query<ArticlesResponseDto>({
      query: GET_ARTICLES_BY_EDITION_ID,
      variables: queryOptions,
      fetchPolicy,
      errorPolicy: 'all',
    })
  }

  getArticleIdByOldId(
    oldArticleId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<
    Apollo.QueryResult<{ Articles: { docs: Pick<ArticlesDto, 'id'>[] } }>
  > {
    return this.apollo.query<{
      Articles: { docs: Pick<ArticlesDto, 'id'>[] }
    }>({
      query: GET_ARTICLE_ID_BY_OLDID,
      variables: { oldArticleId },
      fetchPolicy,
    })
  }

  getArticleIdBySeneId(
    seneArticleId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<
    Apollo.QueryResult<{ Articles: { docs: Pick<ArticlesDto, 'id'>[] } }>
  > {
    return this.apollo.query<{
      Articles: { docs: Pick<ArticlesDto, 'id'>[] }
    }>({
      query: GET_ARTICLE_ID_BY_SENEID,
      variables: { seneArticleId },
      fetchPolicy,
    })
  }

  getArticlesByAuthorId(
    authorId: number,
    page = 1,
    limit = 10,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ObservableQuery.Result<ArticlesResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<ArticlesResponseDto>({
        query: GET_ARTICLES_BY_AUTHOR_ID,
        variables: { page, limit, authorId },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getArticleDetails(
    articleId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ObservableQuery.Result<ArticleDetailsDto, 'complete'>> {
    return this.apollo
      .watchQuery<ArticleDetailsDto>({
        query: GET_ARTICLE_DETAILS,
        variables: { articleId },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getArticleIndex(
    articleId: number
  ): Observable<
    ObservableQuery.Result<
      { articleIndex: { title: string; level: number }[] },
      'complete'
    >
  > {
    return this.apollo
      .watchQuery<{
        articleIndex: { title: string; level: number }[]
      }>({
        query: ARTICLE_INDEX,
        variables: { articleId },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  purchaseArticle(
    articleId: number,
    customer: PurchaseArticleCustomerInput,
    options?: ArticlePurchaseOptions | null
  ): Observable<Apollo.MutateResult<PurchaseArticleResponseDto>> {
    return this.apollo.mutate<PurchaseArticleResponseDto>({
      mutation: PURCHASE_ARTICLE,
      variables: {
        articleId,
        customer,
        options,
      },
    })
  }

  getUserArticlePurchaseById(
    id: number,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<
    ObservableQuery.Result<UserArticlePurchaseResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<UserArticlePurchaseResponseDto>({
        query: GET_USER_ARTICLE_PURCHASE_BY_ID,
        variables: { id },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  loginUser(
    name: string,
    password: string
  ): Observable<LoginUserResponseDto | null | undefined> {
    return this.apollo
      .mutate<LoginUserResponseDto>({
        mutation: LOGIN_USER,
        variables: { name, password },
      })
      .pipe(map(result => result.data))
  }

  getUserMe(
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ObservableQuery.Result<UserMeResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<UserMeResponseDto>({
        query: GET_USER_ME,
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getUsersByOrganization(
    organizationId: number,
    page: number,
    itemsPerPage: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ObservableQuery.Result<UsersResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<UsersResponseDto>({
        query: GET_USERS_BY_ORGANIZATION,
        variables: { organizationId, page, itemsPerPage },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getUserCountByOrganization(
    organizationId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ObservableQuery.Result<UserCountResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<UserCountResponseDto>({
        query: GET_USER_COUNT_BY_ORGANIZATION,
        variables: { organizationId },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getEditors(): Observable<
    ObservableQuery.Result<EditorsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<EditorsResponseDto>({
        query: GET_EDITORS,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getEditorialBoard(
    editionId: number
  ): Observable<ObservableQuery.Result<EditorialBoardResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<EditorialBoardResponseDto>({
        query: GET_EDITORIAL_BOARD,
        variables: { edition: editionId },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  searchAuthors(
    firstName: string,
    lastName: string
  ): Observable<
    ObservableQuery.Result<{ Authors: { docs: { id: number }[] } }, 'complete'>
  > {
    return this.apollo
      .watchQuery<{ Authors: { docs: { id: number }[] } }>({
        query: SEARCH_AUTHORS,
        variables: { firstName, lastName },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  forgotPassword(
    email: string
  ): Observable<Apollo.MutateResult<{ forgotPasswordUser: boolean }>> {
    return this.apollo.mutate<{ forgotPasswordUser: boolean }>({
      mutation: FORGOT_PASSWORD,
      variables: { email },
    })
  }

  resetPassword(
    password: string,
    token: string
  ): Observable<Apollo.MutateResult<ResetPasswordResponseDto>> {
    return this.apollo.mutate<ResetPasswordResponseDto>({
      mutation: RESET_PASSWORD,
      variables: { password, token },
    })
  }

  createUser(
    email: string,
    password: string,
    name: string,
    firstName: string,
    lastName: string,
    recaptchaToken?: string,
    articleToPurchase?: number
  ): Observable<CreateAccountResponseDto | undefined> {
    return this.apollo
      .mutate<CreateUserResponseDto>({
        mutation: CREATE_USER,
        variables: {
          email,
          password,
          name,
          firstName,
          lastName,
          recaptchaToken,
          articleToPurchase,
        },
      })
      .pipe(map(result => result.data?.createUser))
  }

  verifyUser(data: {
    token: string
    purchaseId?: number | null
  }): Observable<VerifyUserResponseDto | undefined> {
    return this.apollo
      .mutate<VerifyUserMutationResponseDto>({
        mutation: VERIFY_USER,
        variables: { data },
      })
      .pipe(map(result => result.data?.verifyUser))
  }

  updateUser(
    id: number,
    data: Partial<UserDto>
  ): Observable<Apollo.MutateResult<UpdateUserResponseDto>> {
    return this.apollo.mutate<UpdateUserResponseDto>({
      mutation: UPDATE_USER,
      variables: { id, autosave: true, data },
    })
  }

  getNotificationOptOuts(): Observable<
    Apollo.QueryResult<NotificationOptOutsResponseDto>
  > {
    return this.apollo.query<NotificationOptOutsResponseDto>({
      query: GET_NOTIFICATION_OPT_OUTS,
      fetchPolicy: 'network-only',
    })
  }

  createNotificationOptOut(
    magazineId: number
  ): Observable<Apollo.MutateResult<CreateNotificationOptOutResponseDto>> {
    return this.apollo.mutate<CreateNotificationOptOutResponseDto>({
      mutation: CREATE_NOTIFICATION_OPT_OUT,
      variables: { magazineId },
    })
  }

  deleteNotificationOptOut(
    id: number
  ): Observable<Apollo.MutateResult<DeleteNotificationOptOutResponseDto>> {
    return this.apollo.mutate<DeleteNotificationOptOutResponseDto>({
      mutation: DELETE_NOTIFICATION_OPT_OUT,
      variables: { id },
    })
  }

  getTotalArticlesCount(): Observable<
    ObservableQuery.Result<TotalArticlesCountResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<TotalArticlesCountResponseDto>({
        query: GET_TOTAL_ARTICLES_COUNT,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getArticlesByMagazineId(
    magazineId: number,
    page = 1,
    limit = 10,
    searchTerm: string,
    rulingDateRange: DateRange | null,
    sources: Set<string> | null,
    articleTypes: Set<string> | null,
    sort?: SortParameter,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ObservableQuery.Result<ArticlesResponseDto, 'complete'>> {
    const $sortTerm = sort
      ? `${sort.descending ? '-' : ''}${sort.property}`
      : undefined
    const trimmedSearchTerm = searchTerm ? searchTerm.trim() : null

    return this.apollo
      .watchQuery<ArticlesResponseDto>({
        query: GET_ARTICLES_BY_MAGAZINE_ID,
        variables: {
          magazineId,
          page,
          limit,
          additionalWhere: {
            AND: [
              ...(rulingDateRange
                ? [
                    {
                      ruling_date: {
                        greater_than_equal: rulingDateRange.dateFrom,
                        less_than: rulingDateRange.dateTo,
                      },
                    },
                  ]
                : []),
              ...(sources ? [{ source: { in: Array.from(sources) } }] : []),
              ...(articleTypes && articleTypes.size > 0
                ? [
                    {
                      OR: Array.from(articleTypes).map(type => ({
                        article_type: {
                          equals: {
                            name: type,
                            title:
                              ARTICLE_TYPE_BACKEND_TITLES[
                                type as ArticleType
                              ] ?? type,
                          },
                        },
                      })),
                    },
                  ]
                : []),
              ...(trimmedSearchTerm
                ? [
                    {
                      OR: [
                        { title: { contains: trimmedSearchTerm } },
                        { keywords__title: { contains: trimmedSearchTerm } },
                        { categories__title: { contains: trimmedSearchTerm } },
                      ],
                    },
                  ]
                : []),
            ],
          },
          sort: $sortTerm,
        },
        fetchPolicy,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getSubscriptions(
    filterOrganizationId?: number
  ): Observable<ObservableQuery.Result<SubscriptionsResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<SubscriptionsResponseDto>({
        query: GET_SUBSCRIPTIONS,
        variables: {
          where: filterOrganizationId
            ? { organization: { equals: filterOrganizationId } }
            : {},
          // Truncated to the start of the day so repeated calls share the
          // same cache key instead of missing the cache on every millisecond.
          currentDate: startOfToday(),
        },
        fetchPolicy: 'cache-first',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getSubscriptionWithPricingDetails(
    id: number
  ): Observable<ObservableQuery.Result<SubscriptionResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<SubscriptionResponseDto>({
        query: GET_SUBSCRIPTION_AND_PRICING,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getSubscriptionsWithPricingDetails(
    page = 1,
    limit = 10,
    filterOrganizationIds?: Set<number>
  ): Observable<ObservableQuery.Result<SubscriptionsResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<SubscriptionsResponseDto>({
        query: GET_SUBSCRIPTIONS_AND_PRICING,
        variables: {
          currentDate: new Date().toISOString(),
          page,
          limit,
          where: filterOrganizationIds
            ? { organization: { in: Array.from(filterOrganizationIds) } }
            : {},
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getProducts(
    limit = -1
  ): Observable<ObservableQuery.Result<ProductsResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<ProductsResponseDto>({
        query: GET_PRODUCTS,
        variables: { limit },
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  /**
   * Configures subscriptions by adding new products and/or updating existing
   * subscriptions. The organizationId parameter is optional and can be used by
   * resellers to specify the organization for which the subscriptions should be
   * configured.
   *
   * @param organizationId - Optional organization id for resellers
   * @param reference - Optional reference for the subscription configuration
   * @param productsToAdd - List of products to add, each with an id, quantity,
   *   optional reference, and
   * @param subscriptionsToUpdate - List of subscriptions to update, each with
   *   an id, optional quantity, reference, endDate, and updateImmediately flag
   * @returns An Observable that resolves to the result of the invoice creation
   *   mutation
   */

  configureSubscription(
    organizationId: number | null,
    reference: string | null,
    productsToAdd:
      | {
          id: number
          quantity: number
          reference?: string | null
          addImmediately?: boolean
        }[]
      | null,
    subscriptionsToUpdate:
      | {
          id: number
          quantity?: number | null
          reference?: string | null
          endDate?: string | null
          updateImmediately?: boolean
        }[]
      | null
  ): Observable<Apollo.MutateResult<ConfigureSubscriptionsResponseDto>> {
    return this.apollo.mutate<ConfigureSubscriptionsResponseDto>({
      mutation: CONFIGURE_SUBSCRIPTIONS,
      variables: {
        organizationId,
        reference,
        productsToAdd,
        subscriptionsToUpdate,
      },
    })
  }

  cancelScheduledChange(
    scheduledChangeId: number
  ): Observable<Apollo.MutateResult<CancelScheduledChangeResponseDto>> {
    return this.apollo.mutate<CancelScheduledChangeResponseDto>({
      mutation: CANCEL_SCHEDULED_CHANGE,
      variables: { scheduledChangeId },
    })
  }

  /**
   * Downloads an article PDF by article ID
   *
   * @param articleId The ID of the article
   * @returns An Observable that resolves to a Blob containing the PDF data
   */
  downloadArticle(articleId: number): Observable<Blob> {
    // Use the correct endpoint path for PDF downloads
    const pdfEndpoint = `${environment.apiUrl}/api/articles/${String(articleId)}/pdf`
    return this.downloadPdf(pdfEndpoint)
  }

  /**
   * Downloads an edition PDF by edition ID
   *
   * @param editionId The ID of the edition
   * @returns An Observable that resolves to a Blob containing the PDF data
   */
  downloadEdition(editionId: number): Observable<Blob> {
    // Use the correct endpoint path for PDF downloads
    const pdfEndpoint = `${environment.apiUrl}/api/editions/${String(editionId)}/pdf`
    return this.downloadPdf(pdfEndpoint)
  }

  /**
   * Fetches organization details by its ID.
   *
   * @param id The numeric ID of the organization.
   * @param fetchPolicy
   * @returns An observable emitting the Organization query result.
   */
  getOrganizationById(
    id: number,
    fetchPolicy: FetchPolicy = 'network-only' // Default to network-only for potentially sensitive/stale data
  ): Observable<Apollo.QueryResult<OrganizationResponseDto>> {
    return this.apollo.query<OrganizationResponseDto>({
      query: GET_ORGANIZATION_BY_ID,
      variables: { id },
      fetchPolicy,
      errorPolicy: 'all', // Example error policy
    })
  }

  /**
   * Fetches organizations by its Reseller ID.
   *
   * @param id
   * @param fetchPolicy
   * @returns An observable emitting the Organization query result.
   */
  getOrganizationsForReseller(
    id: number,
    fetchPolicy: FetchPolicy = 'cache-first' // Default to network-only for potentially sensitive/stale data
  ): Observable<Apollo.QueryResult<OrganizationsResponseDto>> {
    return this.apollo.query<OrganizationsResponseDto>({
      query: GET_ORGANIZATION_FOR_RESELLER,
      variables: {
        where: { reseller: { equals: id } },
      },
      fetchPolicy,
    })
  }

  getResellers(
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<Apollo.QueryResult<AvailableResellersResponseDto>> {
    return this.apollo.query<AvailableResellersResponseDto>({
      query: GET_RESELLERS,
      fetchPolicy,
    })
  }

  getOrganizationResellerRequests(
    resellerOrganizationId: number,
    page?: number,
    limit?: number,
    additionalWhere?: Record<string, unknown>,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<Apollo.QueryResult<OrganizationResellerRequestsResponseDto>> {
    return this.apollo.query<OrganizationResellerRequestsResponseDto>({
      query: GET_ORGANIZATION_RESELLER_REQUESTS,
      variables: {
        page,
        limit,
        where: {
          resellerOrganization: { equals: resellerOrganizationId },
          ...additionalWhere,
        },
      },
      fetchPolicy,
    })
  }

  getOrganizationResellerRequestsForRequester(
    requesterOrganizationId: number,
    page?: number,
    limit?: number,
    additionalWhere?: Record<string, unknown>,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<Apollo.QueryResult<OrganizationResellerRequestsResponseDto>> {
    return this.apollo.query<OrganizationResellerRequestsResponseDto>({
      query: GET_ORGANIZATION_RESELLER_REQUESTS,
      variables: {
        page,
        limit,
        where: {
          requesterOrganization: { equals: requesterOrganizationId },
          ...additionalWhere,
        },
      },
      fetchPolicy,
    })
  }

  getOrganizationResellerRequestById(
    id: number,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<Apollo.QueryResult<OrganizationResellerRequestResponseDto>> {
    return this.apollo.query<OrganizationResellerRequestResponseDto>({
      query: GET_ORGANIZATION_RESELLER_REQUEST_BY_ID,
      variables: { id },
      fetchPolicy,
    })
  }

  findPendingOrganizationResellerRequest(
    requesterOrganizationId: number,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<Apollo.QueryResult<OrganizationResellerRequestsResponseDto>> {
    return this.getOrganizationResellerRequestsForRequester(
      requesterOrganizationId,
      undefined,
      1,
      {
        status: { equals: 'pending' },
      },
      fetchPolicy
    )
  }

  getBillingCyclesByOrganization(
    organizationId: number
  ): Observable<ObservableQuery.Result<BillingCyclesResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<BillingCyclesResponseDto>({
        query: GET_BILLING_CYCLES,
        variables: { organizationId },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  /**
   * Fetches magazines associated with a specific organization ID.
   *
   * @returns
   */
  getUserMagazines(): Observable<
    Apollo.QueryResult<MagazineSubscriptionResponseDto>
  > {
    return this.apollo.query<MagazineSubscriptionResponseDto>({
      query: GET_USER_MAGAZINES,
    })
  }

  updateOrganizationById(
    id: number,
    data: Partial<OrganizationDto>
  ): Observable<Apollo.MutateResult<UpdateOrganizationResponseDto>> {
    return this.apollo.mutate<UpdateOrganizationResponseDto>({
      mutation: UPDATE_ORGANIZATION,
      variables: { id, autosave: true, data },
    })
  }

  updateBillingCycleById(
    id: number,
    data: { reference?: string | null }
  ): Observable<Apollo.MutateResult<UpdateBillingCycleResponseDto>> {
    return this.apollo.mutate<UpdateBillingCycleResponseDto>({
      mutation: UPDATE_BILLING_CYCLE,
      variables: { id, autosave: true, data },
    })
  }

  createOrganization(
    data: Partial<OrganizationDto>
  ): Observable<Apollo.MutateResult<CreateOrganizationResponseDto>> {
    return this.apollo.mutate<CreateOrganizationResponseDto>({
      mutation: CREATE_ORGANIZATION,
      variables: { ...data },
    })
  }

  createOrganizationResellerRequest(data: {
    name?: string | null
    requestedBy: number
    requesterOrganization: number
    resellerOrganization: number
    status: 'pending'
  }): Observable<
    Apollo.MutateResult<CreateOrganizationResellerRequestResponseDto>
  > {
    return this.apollo.mutate<CreateOrganizationResellerRequestResponseDto>({
      mutation: CREATE_ORGANIZATION_RESELLER_REQUEST,
      variables: { data },
    })
  }

  deleteOrganizationResellerRequest(
    id: number
  ): Observable<
    Apollo.MutateResult<DeleteOrganizationResellerRequestResponseDto>
  > {
    return this.apollo.mutate<DeleteOrganizationResellerRequestResponseDto>({
      mutation: DELETE_ORGANIZATION_RESELLER_REQUEST,
      variables: { id },
    })
  }

  acceptOrganizationResellerRequest(
    requestId: number
  ): Observable<
    Apollo.MutateResult<AcceptOrganizationResellerRequestResponseDto>
  > {
    return this.apollo.mutate<AcceptOrganizationResellerRequestResponseDto>({
      mutation: ACCEPT_ORGANIZATION_RESELLER_REQUEST,
      variables: {
        data: { requestId },
      },
    })
  }

  rejectOrganizationResellerRequest(
    requestId: number
  ): Observable<
    Apollo.MutateResult<RejectOrganizationResellerRequestResponseDto>
  > {
    return this.apollo.mutate<RejectOrganizationResellerRequestResponseDto>({
      mutation: REJECT_ORGANIZATION_RESELLER_REQUEST,
      variables: {
        data: { requestId },
      },
    })
  }

  removeReseller(
    organizationId: number
  ): Observable<Apollo.MutateResult<RemoveResellerResponseDto>> {
    return this.apollo.mutate<RemoveResellerResponseDto>({
      mutation: REMOVE_RESELLER,
      variables: { organizationId },
    })
  }
  getInvoicesByOrganization(): Observable<
    ObservableQuery.Result<ListInvoicesResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<ListInvoicesResponseDto>({
        query: GET_INVOICES,
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getSeatAllocationBySubscription(
    subscriptionId: number,
    page: number,
    itemsPerPage: number
  ): Observable<
    ObservableQuery.Result<SeatAllocationsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<SeatAllocationsResponseDto>({
        query: SEAT_ALLOCATION_BY_SUBSCRIPTION,
        variables: { subscriptionId, page, itemsPerPage },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  createSeatAllocation(
    subscriptionId: number,
    userId: number
  ): Observable<Apollo.MutateResult<CreateSeatAllocationResponseDto>> {
    return this.apollo.mutate<CreateSeatAllocationResponseDto>({
      mutation: CREATE_SEAT_ALLOCATION,
      variables: { subscriptionId, userId },
    })
  }

  deleteSeatAllocation(
    seatAllocationId: number
  ): Observable<Apollo.MutateResult<DeleteSeatAllocationResponseDto>> {
    return this.apollo.mutate<DeleteSeatAllocationResponseDto>({
      mutation: DELETE_SEAT_ALLOCATION,
      variables: { seatAllocationId: seatAllocationId },
    })
  }

  getSeatAllocationsByUser(
    userId: number,
    page: number,
    itemsPerPage: number
  ): Observable<
    ObservableQuery.Result<SeatAllocationsResponseDto, 'complete'>
  > {
    return this.apollo
      .watchQuery<SeatAllocationsResponseDto>({
        query: SEAT_ALLOCATION_BY_USER,
        variables: { userId, page, itemsPerPage },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  domainsByOrganization(
    organizationId: number
  ): Observable<Apollo.QueryResult<DomainNamesResponseDto>> {
    return this.apollo.query<DomainNamesResponseDto>({
      query: GET_DOMAINS_BY_ORG,
      variables: { organizationId },
      fetchPolicy: 'network-only',
    })
  }

  validateOrganizationDomains(
    domains: Set<string>
  ): Observable<Apollo.QueryResult<ValidateOrganizationDomainsResponse>> {
    return this.apollo.query<ValidateOrganizationDomainsResponse>({
      query: VALIDATE_ORGANIZATION_DOMAINS,
      variables: { domains: Array.from(domains) },
    })
  }

  addDomainForOrganization(
    organizationId: number,
    domain: string
  ): Observable<Apollo.MutateResult<CreateDomainNameResponseDto>> {
    return this.apollo.mutate<CreateDomainNameResponseDto>({
      mutation: ADD_DOMAINNAME,
      variables: { organizationId, domain },
    })
  }

  removeDomainFromOrganization(
    domainId: number
  ): Observable<Apollo.MutateResult<DeleteDomainNameResponseDto>> {
    return this.apollo.mutate<DeleteDomainNameResponseDto>({
      mutation: REMOVE_DOMAINNAME,
      variables: { domainId },
    })
  }

  private downloadPdf(pdfEndpoint: string): Observable<Blob> {
    // Add role as query parameter
    const url = new URL(pdfEndpoint)
    url.searchParams.append('role', 'customer')
    url.searchParams.append('t', Date.now().toString()) // Prevent caching
    const token = localStorage.getItem('token') ?? ''

    // Create headers with only the necessary Bearer token authentication
    const headers = new HttpHeaders({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    })

    // Make a GET request to the authenticated endpoint
    return this.http
      .get(url.toString(), {
        headers,
        responseType: 'blob',
      })
      .pipe(
        catchError((error: unknown) => {
          if (error instanceof Blob) {
            return new Observable<Blob>(observer => {
              const reader = new FileReader()
              reader.onload = () => {
                observer.error(new Error('Failed to download PDF'))
              }
              reader.readAsText(error)
            })
          }
          return throwError(() => new Error('Failed to download PDF'))
        })
      )
  }

  getAllFAQs(
    locale: string
  ): Observable<ObservableQuery.Result<FAQResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<FAQResponseDto>({
        query: GET_ALL_FAQS_QUERY,
        variables: {
          limit: -1,
          locale: locale,
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  /**
   * A random advertisement for `placement`, or a result carrying `null` when
   * nothing is booked for this page right now.
   *
   * `no-cache` on purpose: the result is random by definition, so caching it
   * would pin the first banner for the rest of the session. It also keeps
   * Apollo from normalising on `Advertisement:id` and sharing one banner across
   * placements.
   *
   * The API rejects (HTTP 400) a `referenceId` on the home placement and a
   * non-positive-integer id anywhere, so both are filtered out here rather than
   * at each call site.
   */
  getRandomAdvertisement(
    placement: AdvertisementPlacement,
    referenceId?: number | null
  ): Observable<Apollo.QueryResult<RandomAdvertisementResponseDto>> {
    const reference =
      placement !== 'home' &&
      typeof referenceId === 'number' &&
      Number.isInteger(referenceId) &&
      referenceId > 0
        ? referenceId
        : null

    return this.apollo.query<RandomAdvertisementResponseDto>({
      query: GET_RANDOM_ADVERTISEMENT,
      variables: { placement, referenceId: reference },
      fetchPolicy: 'no-cache',
    })
  }

  getPageBySlug(
    slug: string,
    locale: string
  ): Observable<ObservableQuery.Result<PageResponseDto, 'complete'>> {
    return this.apollo
      .watchQuery<PageResponseDto>({
        query: GET_PAGE_BY_SLUG_QUERY,
        variables: {
          slug: slug,
          locale: locale,
        },
        // Not `no-cache`: that would bypass the cache handed over from the
        // server render, so hydration would refetch the page it just received.
        // Safe because the cache key includes the locale and `Page` is stored
        // embedded (see the type policies in graphql.module.ts).
        fetchPolicy: 'cache-first',
      })
      .valueChanges.pipe(completeDataOrThrow())
  }

  getProrationFactor(
    organizationId?: number
  ): Observable<Apollo.QueryResult<GetProrationFactorDto>> {
    console.log('Fetching proration factor for organizationId:', organizationId)

    return this.apollo.query<GetProrationFactorDto>({
      query: GET_PRORATION_FACTOR,
      variables: {
        organizationId,
      },
      fetchPolicy: 'network-only',
    })
  }
}

export interface SortParameter {
  property: string
  descending?: boolean
}
